import {
  type APIChannel,
  can,
  canInGuild,
  type ChannelGroup,
  ChannelType,
  PermissionFlags,
  selectActiveThreads,
  selectSidebarTree,
  selectIsChannelMuted,
  selectIsUnread,
  selectMentionCount,
  selectPrivateChannels,
} from '@alternativamais/chat-core';
import { memo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Avatar } from '../../componentes/Avatar';
import { avisarErro } from '../../componentes/Avisos';
import {
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
  SeparadorMenu,
} from '../../componentes/Flutuante';
import { Icone, type NomeIcone } from '../../componentes/Icone';
import { abrirCamada } from '../../chat/camadas';
import {
  useCliente,
  useDerivado,
  useEstado,
  useEstadoRaso,
} from '../../chat/contexto';
import { useNome, useModoDesenvolvedor } from '../../chat/ganchos';
import { IconeDoGrupo } from './cabecalho/Cabecalho';
import {
  DURACOES_SILENCIO,
  useSilenciarCanal,
} from './cabecalho/MenuNotificacoes';
import { useEventos } from './eventos/comum';
import { NovoGrupo } from './dms/NovoGrupo';
import {
  avisarCopiado,
  TextoDoStatus,
  useStatusPersonalizado,
} from './perfil/perfil';
import { iconeDoCanal } from '../../utilidades/canais';
import { PainelUsuario } from './PainelUsuario';

/** Barra de canais do servidor (240 px): categorias, canais e threads em que entrei. */
export function LateralServidor({
  guildId,
  canalAtual,
}: {
  guildId: string;
  canalAtual: string | null;
}) {
  const nome = useEstado((estado) => estado.guilds[guildId]?.name ?? '');
  const arvore = useDerivado(
    (estado) => selectSidebarTree(estado, guildId),
    (estado) => [
      guildId,
      estado.guildChannelIds[guildId],
      estado.channels,
      estado.members[guildId],
      estado.guilds[guildId],
      estado.guildSettings[guildId],
    ],
  );
  return (
    <aside
      aria-label={`Canais de ${nome}`}
      className="flex w-60 shrink-0 flex-col bg-lateral"
    >
      <MenuDoServidor guildId={guildId} nome={nome} />
      <nav className="rolagem min-h-0 grow overflow-y-auto px-2 pt-2 pb-4">
        <EntradaEventos guildId={guildId} />
        {arvore.map((grupo) => (
          <Categoria
            key={grupo.category?.id ?? 'sem-categoria'}
            guildId={guildId}
            grupo={grupo}
            canalAtual={canalAtual}
          />
        ))}
      </nav>
      <PainelUsuario />
    </aside>
  );
}

function Categoria({
  guildId,
  grupo,
  canalAtual,
}: {
  guildId: string;
  grupo: ChannelGroup;
  canalAtual: string | null;
}) {
  const cliente = useCliente();
  const categoria = grupo.category;
  const recolhidaSalva = useEstado((estado) =>
    categoria
      ? !!estado.guildSettings[guildId]?.channel_overrides.find(
          (item) => item.channel_id === categoria.id,
        )?.collapsed
      : false,
  );
  const [recolhida, setRecolhida] = useState<boolean | null>(null);
  const fechada = recolhida ?? recolhidaSalva;
  const algumaNaoLida = useEstado((estado) =>
    grupo.channels.some(
      (canal) =>
        selectIsUnread(estado, canal.id) &&
        !selectIsChannelMuted(estado, canal.id),
    ),
  );
  const alternar = () => {
    const novo = !fechada;
    setRecolhida(novo);
    if (categoria) {
      cliente.api
        .updateGuildSettings(guildId, {
          channel_overrides: { [categoria.id]: { collapsed: novo } },
        })
        .catch(() => undefined);
    }
  };
  return (
    <div className="flex flex-col gap-0.5 pt-3.5 first:pt-0">
      {categoria && (
        <button
          type="button"
          aria-expanded={!fechada}
          onClick={alternar}
          className={`flex h-6 items-center gap-0.5 pr-1 pl-0.5 text-left hover:text-texto ${
            fechada && algumaNaoLida ? 'text-texto' : 'text-texto-3'
          }`}
        >
          <Icone
            nome={fechada ? 'chevron-direita' : 'chevron-baixo'}
            tamanho={12}
          />
          <span className="truncate text-xs font-semibold tracking-[.04em] uppercase">
            {categoria.name}
          </span>
        </button>
      )}
      {grupo.channels.map((canal) => (
        <ItemCanal
          key={canal.id}
          canal={canal}
          selecionado={canal.id === canalAtual}
          recolhido={fechada}
          canalAtual={canalAtual}
        />
      ))}
    </div>
  );
}

const ItemCanal = memo(function ItemCanal({
  canal,
  selecionado,
  recolhido,
  canalAtual,
}: {
  canal: APIChannel;
  selecionado: boolean;
  recolhido: boolean;
  canalAtual: string | null;
}) {
  const navegar = useNavigate();
  const naoLido = useEstado((estado) => selectIsUnread(estado, canal.id));
  const mencoes = useEstado((estado) => selectMentionCount(estado, canal.id));
  const silenciado = useEstado((estado) =>
    selectIsChannelMuted(estado, canal.id),
  );
  const threads = useDerivado(
    (estado) =>
      selectActiveThreads(estado, canal.id).filter(
        (thread) => estado.joinedThreads[thread.id],
      ),
    (estado) => [
      estado.guildThreadIds[canal.guild_id ?? ''],
      estado.channels,
      estado.joinedThreads,
    ],
  );
  const [menu, setMenu] = useState<Posicao | null>(null);
  const destaque = (naoLido && !silenciado) || mencoes > 0;
  if (
    recolhido &&
    !selecionado &&
    !destaque &&
    !threads.some((thread) => thread.id === canalAtual)
  )
    return null;
  return (
    <div
      className="relative flex flex-col"
      onContextMenu={(evento) => {
        // Só no canal (as threads embaixo dele não abrem este menu).
        if (!(evento.target as HTMLElement).closest('[data-canal]')) return;
        evento.preventDefault();
        setMenu({ x: evento.clientX, y: evento.clientY });
      }}
    >
      {destaque && !selecionado && (
        <span
          aria-hidden="true"
          className="absolute top-3 -left-2 h-2 w-1 rounded-r bg-texto"
        />
      )}
      <button
        type="button"
        data-canal
        aria-current={selecionado ? 'page' : undefined}
        onClick={() => navegar(`/canais/${canal.guild_id}/${canal.id}`)}
        className={`flex h-8 items-center gap-1.5 rounded-md px-2 text-left ${
          selecionado
            ? 'bg-ativo text-texto'
            : destaque
              ? 'text-texto hover:bg-hover'
              : silenciado
                ? 'text-silenciado hover:bg-hover hover:text-texto-2'
                : 'text-texto-3 hover:bg-hover hover:text-texto-2'
        } disabled:cursor-default disabled:opacity-60`}
      >
        <Icone
          nome={iconeDoCanal(canal)}
          tamanho={20}
          className="shrink-0 opacity-80"
        />
        <span
          className={`min-w-0 grow truncate text-[15px] ${destaque && !selecionado ? 'font-semibold' : 'font-medium'}`}
        >
          {canal.name}
        </span>
        {mencoes > 0 && (
          <span
            aria-label={`${mencoes} ${mencoes === 1 ? 'menção' : 'menções'}`}
            className="flex h-4 min-w-4 items-center justify-center rounded-lg bg-perigo px-[5px] text-[11px] font-bold text-white"
          >
            {mencoes}
          </span>
        )}
      </button>
      {threads.map((thread) => (
        <ItemThread
          key={thread.id}
          thread={thread}
          selecionado={thread.id === canalAtual}
        />
      ))}
      {menu && (
        <MenuCanal canal={canal} ancora={menu} aoFechar={() => setMenu(null)} />
      )}
    </div>
  );
});

/** Cabeçalho da lateral: o nome do servidor abre o menu dele (C16). */
function MenuDoServidor({ guildId, nome }: { guildId: string; nome: string }) {
  const [menu, setMenu] = useState<Posicao | null>(null);
  const pode = useEstadoRaso((estado) => ({
    convidar: (estado.guildChannelIds[guildId] ?? []).some((id) =>
      can(estado, id, PermissionFlags.CREATE_INSTANT_INVITE),
    ),
    configurar: [
      PermissionFlags.MANAGE_GUILD,
      PermissionFlags.MANAGE_ROLES,
      PermissionFlags.MANAGE_CHANNELS,
      PermissionFlags.KICK_MEMBERS,
      PermissionFlags.BAN_MEMBERS,
      PermissionFlags.MODERATE_MEMBERS,
      PermissionFlags.VIEW_AUDIT_LOG,
      PermissionFlags.MANAGE_GUILD_EXPRESSIONS,
      PermissionFlags.CREATE_GUILD_EXPRESSIONS,
    ].some((flag) => canInGuild(estado, guildId, flag)),
    canais: canInGuild(estado, guildId, PermissionFlags.MANAGE_CHANNELS),
    eventos:
      canInGuild(estado, guildId, PermissionFlags.CREATE_EVENTS) ||
      canInGuild(estado, guildId, PermissionFlags.MANAGE_EVENTS),
    ocultos: !!estado.guildSettings[guildId]?.hide_muted_channels,
  }));
  const cliente = useCliente();
  const fazer = (acao: () => void) => () => {
    setMenu(null);
    acao();
  };
  return (
    <header className="relative shrink-0 border-b border-divisor">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={!!menu}
        onClick={(evento) => {
          const caixa = evento.currentTarget.getBoundingClientRect();
          setMenu(menu ? null : { x: caixa.left + 8, y: caixa.bottom + 4 });
        }}
        className="flex h-12 w-full items-center gap-2 px-4 text-left hover:bg-hover"
      >
        <h2 className="m-0 min-w-0 grow truncate text-[15px] font-bold">
          {nome}
        </h2>
        <Icone
          nome={menu ? 'x' : 'chevron-baixo'}
          tamanho={16}
          className="shrink-0 text-texto-2"
        />
      </button>
      {menu && (
        <Flutuante
          ancora={menu}
          aoFechar={() => setMenu(null)}
          rotulo={`Menu de ${nome}`}
          papel="menu"
        >
          <CaixaMenu largura={248}>
            {pode.convidar && (
              <ItemMenu
                icone="usuario-mais"
                aoEscolher={fazer(() =>
                  abrirCamada({ tipo: 'convidar', guildId }),
                )}
              >
                <span className="text-destaque-claro">Convidar pessoas</span>
              </ItemMenu>
            )}
            {pode.configurar && (
              <ItemMenu
                icone="engrenagem"
                aoEscolher={fazer(() =>
                  abrirCamada({ tipo: 'config-servidor', guildId }),
                )}
              >
                Configurações do servidor
              </ItemMenu>
            )}
            {pode.canais && (
              <>
                <ItemMenu
                  icone="mais"
                  aoEscolher={fazer(() =>
                    abrirCamada({ tipo: 'criar-canal', guildId }),
                  )}
                >
                  Criar canal
                </ItemMenu>
                <ItemMenu
                  icone="lista"
                  aoEscolher={fazer(() =>
                    abrirCamada({ tipo: 'criar-categoria', guildId }),
                  )}
                >
                  Criar categoria
                </ItemMenu>
              </>
            )}
            {pode.eventos && (
              <ItemMenu
                icone="calendario"
                aoEscolher={fazer(() =>
                  abrirCamada({ tipo: 'criar-evento', guildId }),
                )}
              >
                Criar evento
              </ItemMenu>
            )}
            <SeparadorMenu />
            <ItemMenu
              icone="sino"
              aoEscolher={fazer(() =>
                abrirCamada({ tipo: 'notificacoes-servidor', guildId }),
              )}
            >
              Configurações de notificação
            </ItemMenu>
            <ItemMenu
              icone="editar"
              aoEscolher={fazer(() =>
                abrirCamada({
                  tipo: 'config-usuario',
                  secao: 'perfil-servidor',
                }),
              )}
            >
              Editar perfil no servidor
            </ItemMenu>
            <ItemMenu
              icone={pode.ocultos ? 'check' : 'sino-mudo'}
              aoEscolher={fazer(() =>
                cliente.api
                  .updateGuildSettings(guildId, {
                    hide_muted_channels: !pode.ocultos,
                  })
                  .then((ajustes) =>
                    cliente.store
                      .getState()
                      .dispatch('USER_GUILD_SETTINGS_UPDATE', ajustes),
                  )
                  .catch(avisarErro),
              )}
            >
              Ocultar canais silenciados
            </ItemMenu>
            <SeparadorMenu />
            <ItemMenu
              perigo
              icone="sair"
              aoEscolher={fazer(() =>
                abrirCamada({ tipo: 'sair-servidor', guildId }),
              )}
            >
              Sair do servidor
            </ItemMenu>
          </CaixaMenu>
        </Flutuante>
      )}
    </header>
  );
}

/** Menu do canal (C18, botão direito): lido, link, silenciar, ID. */
function MenuCanal({
  canal,
  ancora,
  aoFechar,
}: {
  canal: APIChannel;
  ancora: Posicao;
  aoFechar: () => void;
}) {
  const dev = useModoDesenvolvedor();
  const cliente = useCliente();
  const silenciar = useSilenciarCanal(canal.id);
  const silenciado = useEstado((estado) =>
    selectIsChannelMuted(estado, canal.id),
  );
  const naoLido = useEstado((estado) => selectIsUnread(estado, canal.id));
  const [duracoes, setDuracoes] = useState(false);
  const podeEditar = useEstado((estado) =>
    can(estado, canal.id, PermissionFlags.MANAGE_CHANNELS),
  );
  const podeConvidar = useEstado((estado) =>
    can(estado, canal.id, PermissionFlags.CREATE_INSTANT_INVITE),
  );
  const fazer = (acao: () => unknown) => () => {
    aoFechar();
    acao();
  };
  return (
    <Flutuante
      ancora={ancora}
      aoFechar={aoFechar}
      rotulo={`Opções de #${canal.name}`}
      papel="menu"
    >
      <CaixaMenu largura={240}>
        <ItemMenu
          icone="check"
          aoEscolher={fazer(() => {
            if (naoLido) cliente.ack(canal.id).catch(avisarErro);
          })}
        >
          Marcar como lido
        </ItemMenu>
        <ItemMenu
          icone="link"
          aoEscolher={fazer(() =>
            avisarCopiado(
              `${window.location.origin}/canais/${canal.guild_id}/${canal.id}`,
              'Link copiado.',
            ),
          )}
        >
          Copiar link
        </ItemMenu>
        {podeConvidar && canal.guild_id && (
          <ItemMenu
            icone="usuario-mais"
            aoEscolher={fazer(() =>
              abrirCamada({
                tipo: 'convidar',
                guildId: canal.guild_id!,
                canalId: canal.id,
              }),
            )}
          >
            Convidar pessoas
          </ItemMenu>
        )}
        <SeparadorMenu />
        {silenciado ? (
          <ItemMenu
            icone="sino"
            aoEscolher={fazer(() =>
              silenciar({ reativar: true }).catch(avisarErro),
            )}
          >
            Reativar canal
          </ItemMenu>
        ) : (
          <ItemMenu icone="sino-mudo" aoEscolher={() => setDuracoes(!duracoes)}>
            Silenciar canal
          </ItemMenu>
        )}
        {duracoes &&
          DURACOES_SILENCIO.map((duracao) => (
            <ItemMenu
              key={duracao.rotulo}
              aoEscolher={fazer(() =>
                silenciar({ silenciar: duracao.minutos }).catch(avisarErro),
              )}
            >
              <span className="pl-6">{duracao.rotulo}</span>
            </ItemMenu>
          ))}
        {podeEditar && (
          <>
            <SeparadorMenu />
            <ItemMenu
              icone="engrenagem"
              aoEscolher={fazer(() =>
                abrirCamada({ tipo: 'config-canal', canalId: canal.id }),
              )}
            >
              Editar canal
            </ItemMenu>
          </>
        )}
        <SeparadorMenu />
        {dev && (
          <ItemMenu
            icone="copiar"
            aoEscolher={fazer(() => avisarCopiado(canal.id, 'ID copiado.'))}
          >
            Copiar ID do canal
          </ItemMenu>
        )}
      </CaixaMenu>
    </Flutuante>
  );
}

/** "Eventos" no topo da lista de canais (como o Discord), com os próximos. */
function EntradaEventos({ guildId }: { guildId: string }) {
  const eventos = useEventos(guildId);
  const acontecendo = eventos.some((evento) => evento.status === 2);
  return (
    <button
      type="button"
      onClick={() => abrirCamada({ tipo: 'eventos', guildId })}
      className="mb-2 flex h-8 w-full items-center gap-1.5 rounded-md px-2 text-left text-texto-3 hover:bg-hover hover:text-texto-2"
    >
      <Icone nome="calendario" tamanho={20} className="shrink-0 opacity-80" />
      <span className="grow text-[15px] font-medium">
        {eventos.length
          ? `${eventos.length} ${eventos.length === 1 ? 'evento' : 'eventos'}`
          : 'Eventos'}
      </span>
      {acontecendo && (
        <span className="rounded bg-[rgba(61,214,140,.14)] px-1.5 text-[11px] font-bold text-online">
          AGORA
        </span>
      )}
    </button>
  );
}

function ItemThread({
  thread,
  selecionado,
}: {
  thread: APIChannel;
  selecionado: boolean;
}) {
  const navegar = useNavigate();
  const naoLido = useEstado((estado) => selectIsUnread(estado, thread.id));
  return (
    <button
      type="button"
      onClick={() => navegar(`/canais/${thread.guild_id}/${thread.id}`)}
      className={`flex h-[30px] items-center gap-2 rounded-md pr-2 pl-[18px] text-left text-sm ${
        selecionado
          ? 'bg-ativo text-texto'
          : naoLido
            ? 'font-semibold text-texto hover:bg-hover'
            : 'text-texto-3 hover:bg-hover'
      }`}
    >
      <Icone nome="thread" tamanho={16} className="shrink-0" />
      <span className="truncate">{thread.name}</span>
    </button>
  );
}

/**
 * Lateral do início (D1): encontrar ou iniciar conversa, Pessoas, Threads
 * que sigo e as conversas diretas e em grupo (o "+" cria um grupo).
 */
export function LateralDms({ canalAtual }: { canalAtual: string | null }) {
  const navegar = useNavigate();
  const [novoGrupo, setNovoGrupo] = useState<Posicao | null>(null);
  const conversas = useDerivado(selectPrivateChannels, (estado) => [
    estado.privateChannelIds,
    estado.channels,
  ]);
  const threadsNaoLidas = useEstado(
    (estado) =>
      Object.keys(estado.joinedThreads).filter(
        (id) => selectMentionCount(estado, id) > 0,
      ).length,
  );
  return (
    <aside
      aria-label="Mensagens diretas"
      className="flex w-60 shrink-0 flex-col bg-lateral"
    >
      <div className="flex h-12 shrink-0 items-center border-b border-divisor px-2.5">
        <button
          type="button"
          onClick={() => abrirCamada({ tipo: 'nova-conversa' })}
          className="h-7 w-full truncate rounded-md bg-rail px-2 text-left text-[13px] font-medium text-texto-3 hover:text-texto-2"
        >
          Encontrar ou iniciar conversa
        </button>
      </div>
      <nav
        aria-label="Início"
        className="flex shrink-0 flex-col gap-0.5 px-2 pt-2"
      >
        <AtalhoInicio
          icone="pessoas"
          rotulo="Pessoas"
          atual={canalAtual === null}
          aoClicar={() => navegar('/canais/@me')}
        />
        <AtalhoInicio
          icone="thread"
          rotulo="Threads que sigo"
          atual={canalAtual === 'threads'}
          aoClicar={() => navegar('/canais/@me/threads')}
          contagem={threadsNaoLidas}
        />
      </nav>
      <div className="flex h-7 shrink-0 items-center justify-between pt-4 pr-2.5 pl-[18px] text-texto-3">
        <h2 className="m-0 text-xs font-semibold tracking-[.04em] uppercase">
          Mensagens diretas
        </h2>
        <button
          type="button"
          aria-label="Criar conversa em grupo"
          title="Criar conversa em grupo"
          onClick={(evento) => {
            const caixa = evento.currentTarget.getBoundingClientRect();
            setNovoGrupo(
              novoGrupo ? null : { x: caixa.left, y: caixa.bottom + 6 },
            );
          }}
          className="flex size-6 items-center justify-center rounded-md hover:text-texto"
        >
          <Icone nome="mais" tamanho={16} />
        </button>
      </div>
      <nav
        aria-label="Conversas"
        className="rolagem min-h-0 grow overflow-y-auto px-2 pt-1 pb-4"
      >
        {conversas.length === 0 && (
          <p className="px-2 py-3 text-sm text-texto-3">
            Nenhuma conversa ainda.
          </p>
        )}
        {conversas.map((canal) => (
          <ItemConversa
            key={canal.id}
            canal={canal}
            selecionado={canal.id === canalAtual}
          />
        ))}
      </nav>
      <PainelUsuario />
      {novoGrupo && (
        <Flutuante
          ancora={novoGrupo}
          aoFechar={() => setNovoGrupo(null)}
          rotulo="Criar conversa em grupo"
        >
          <NovoGrupo aoFechar={() => setNovoGrupo(null)} />
        </Flutuante>
      )}
    </aside>
  );
}

function AtalhoInicio({
  icone,
  rotulo,
  atual,
  aoClicar,
  contagem = 0,
}: {
  icone: NomeIcone;
  rotulo: string;
  atual: boolean;
  aoClicar: () => void;
  contagem?: number;
}) {
  return (
    <button
      type="button"
      aria-current={atual ? 'page' : undefined}
      onClick={aoClicar}
      className={`flex h-[42px] items-center gap-3 rounded-md px-2.5 text-left text-[15px] font-medium ${
        atual
          ? 'bg-ativo text-texto'
          : 'text-texto-2 hover:bg-hover hover:text-texto'
      }`}
    >
      <Icone nome={icone} tamanho={22} />
      <span className="grow">{rotulo}</span>
      {contagem > 0 && (
        <span className="flex h-4 min-w-4 items-center justify-center rounded-lg bg-perigo px-[5px] text-[11px] font-bold text-white">
          {contagem}
        </span>
      )}
    </button>
  );
}

function ItemConversa({
  canal,
  selecionado,
}: {
  canal: APIChannel;
  selecionado: boolean;
}) {
  const navegar = useNavigate();
  const cliente = useCliente();
  const naoLido = useEstado((estado) => selectIsUnread(estado, canal.id));
  const mencoes = useEstado((estado) => selectMentionCount(estado, canal.id));
  const pessoa = canal.recipients?.[0];
  const nomePessoa = useNome(pessoa?.id ?? '', null, pessoa);
  const status = useEstado((estado) =>
    pessoa ? (estado.presences[pessoa.id]?.status ?? 'offline') : 'offline',
  );
  const personalizado = useStatusPersonalizado(pessoa?.id ?? '');
  const grupo = canal.type === ChannelType.GROUP_DM;
  const titulo = grupo
    ? canal.name ||
      (canal.recipients ?? [])
        .map((item) => item.global_name ?? item.username)
        .join(', ')
    : nomePessoa;
  const fechar = () => {
    if (grupo) {
      abrirCamada({ tipo: 'sair-grupo', canalId: canal.id });
      return;
    }
    cliente.api
      .deleteChannel(canal.id)
      .then(() => {
        if (selecionado) navegar('/canais/@me');
      })
      .catch(avisarErro);
  };
  return (
    <div className="group relative">
      {naoLido && !selecionado && (
        <span
          aria-hidden="true"
          className="absolute top-[17px] -left-2 h-2 w-1 rounded-r bg-texto"
        />
      )}
      <button
        type="button"
        aria-current={selecionado ? 'page' : undefined}
        onClick={() => navegar(`/canais/@me/${canal.id}`)}
        className={`flex h-[42px] w-full items-center gap-2.5 rounded-md pr-8 pl-2 text-left ${
          selecionado
            ? 'bg-ativo text-texto'
            : naoLido
              ? 'text-texto hover:bg-hover'
              : 'text-texto-3 hover:bg-hover hover:text-texto-2'
        }`}
      >
        {pessoa && !grupo ? (
          <Avatar
            pessoa={pessoa}
            nome={titulo}
            tamanho={32}
            status={status}
            fundo={selecionado ? 'var(--cor-ativo)' : 'var(--cor-lateral)'}
          />
        ) : (
          <IconeDoGrupo canal={canal} tamanho={32} />
        )}
        <span className="flex min-w-0 grow flex-col">
          <span
            className={`truncate text-[15px] ${naoLido ? 'font-semibold' : 'font-medium'}`}
          >
            {titulo}
          </span>
          {grupo ? (
            <span className="text-xs font-medium text-texto-3">
              {(canal.recipients?.length ?? 0) + 1} membros
            </span>
          ) : (
            personalizado &&
            pessoa && (
              <TextoDoStatus
                userId={pessoa.id}
                className="text-xs font-medium text-texto-3"
              />
            )
          )}
        </span>
        {mencoes > 0 && (
          <span className="flex h-4 min-w-4 items-center justify-center rounded-lg bg-perigo px-[5px] text-[11px] font-bold text-white">
            {mencoes}
          </span>
        )}
      </button>
      <button
        type="button"
        aria-label={
          grupo ? `Sair do grupo ${titulo}` : `Fechar conversa com ${titulo}`
        }
        title={grupo ? 'Sair do grupo' : 'Fechar conversa'}
        onClick={fechar}
        className="absolute top-[11px] right-2 hidden size-5 items-center justify-center rounded text-texto-3 group-focus-within:flex group-hover:flex hover:text-texto"
      >
        <Icone nome="x" tamanho={16} />
      </button>
    </div>
  );
}
